import { CSSInterpolation } from "@emotion/css/dist/declarations/src/create-instance";
import { Theme } from '@mui/material/styles';

// MUI sx prop을 위한 스타일 정의
export const rubricTableStyles = {
	tableContainer: {
		boxShadow: 'none',
		borderRadius: '0px',
		overflow: 'hidden',
		width: '100%',
		maxWidth: '100%',
    '& *': {
      fontSize: '1rem',
    },
		'& .MuiTable-root': {
			borderCollapse: 'collapse',
			width: '100%',
		}
	},

	// 테이블 헤더 스타일
	tableHead: {
		background: '#f2f7f3',
		'& .MuiTableCell-head': {
			color: '#000000',
			fontWeight: 600,
			padding: '8px',
			border: '1px solid #cbcbcb',
			textAlign: 'center',
			'&:last-child': {
				borderLeft: 'none',
			}
		}
	},

	// 테이블 바디 스타일
	tableBody: {
		'& .MuiTableRow-root': {
			'&:hover': {
				backgroundColor: 'rgba(242, 247, 243, 0.3)',
			},
		},
		'& .MuiTableCell-root': {
			padding: '16px 8px',
			border: '1px solid #cbcbcb',
			wordWrap: 'break-word',
			wordBreak: 'break-word',
			whiteSpace: 'normal',
			overflowWrap: 'break-word',
		}
	},

	// 루브릭 이름 셀 스타일
	rubricNameCell: {
		backgroundColor: '#ffffff',
		fontWeight: 600,
		verticalAlign: 'middle',
		textAlign: 'center',
		'& .MuiTypography-root': {
			fontWeight: 700,
      color: "#0126fc",
			'&.rubric-title': {
				color: '#0066cc',
				fontWeight: 600,
			},
			'&.rubric-score': {
				color: '#000000',
				fontWeight: 700,
			}
		}
	},

	// 점수 텍스트 스타일
	scoreText: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '4px',
		fontWeight: 600,
	},

	// 점수 배지 스타일
	scoreBadge: {
		display: 'inline-block',
		padding: '4px 8px',
		background: 'transparent',
		borderRadius: '0px',
		marginTop: '4px',
	},

	// 평가 기준 셀 스타일
	standardCell: {
		backgroundColor: '#ffffff',
		textAlign: 'center',
	},

	// 평가 항목 아이템 셀 스타일
	itemNameCell: {
		backgroundColor: '#ffffff',
		fontWeight: 600,
		color: '#000000',
		textAlign: 'center',
	},

	// shared 버전 item row 스타일
	sharedItemRow: {
		display: 'flex',
		height: '100%',
		'& .item-name': {
			width: '15%',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			borderRight: '1px solid #e0e0e0',
			padding: '12px',
			backgroundColor: '#f8f9fa',
			fontWeight: 500,
		},
		'& .item-standard': {
			flex: 1,
			padding: '12px 16px',
			backgroundColor: '#ffffff',
			display: 'flex',
			alignItems: 'center',
		}
	},

	// Export 버전 스타일
	exportTable: {
		marginTop: '20px',
	},

	// 애니메이션 스타일
	fadeIn: {
		animation: 'fadeIn 0.3s ease-in',
		'@keyframes fadeIn': {
			'0%': {
				opacity: 0,
				transform: 'translateY(-4px)',
			},
			'100%': {
				opacity: 1,
				transform: 'translateY(0)',
			}
		}
	},

	// 반응형 스타일
	responsive: {
		'@media (max-width: 768px)': {
			'& .MuiTableCell-root': {
				padding: '10px 12px',
			},
			'& .MuiTableCell-head': {
				padding: '12px',
			}
		}
	},

	// 3단계 표 스타일 (루브릭 이름, 등급, 점수)
	threeLevelTable: {
		'& .MuiTableHead-root': {
			'& .MuiTableCell-head': {
				backgroundColor: '#f2f7f3',
				color: '#01261c',
				borderBottom: '2px solid #cbcbcb',
				fontWeight: 600,
				textAlign: 'center',
			}
		},
		'& .MuiTableBody-root': {
			'& .MuiTableRow-root': {
				'&:hover': {
					backgroundColor: 'rgba(242, 247, 243, 0.3)',
				},
				'& .MuiTableCell-root': {
					borderBottom: '1px solid #e0e0e0',
					textAlign: 'center',
					padding: '12px 8px',
				}
			}
		}
	},

	// 3단계 표 루브릭 이름 셀 스타일
	threeLevelRubricCell: {
		backgroundColor: '#f8f9fa',
		fontWeight: 600,
		color: '#2c3e50',
		textAlign: 'center',
		verticalAlign: 'middle',
		'& .rubric-title': {
			color: '#0066cc',
			marginBottom: '4px',
		},
		'& .rubric-score': {
			fontWeight: 700,
			color: '#01261c',
		}
	},

	// 3단계 표 등급 셀 스타일
	threeLevelGradeCell: {
		fontWeight: 600,
		color: '#01261c',
	},

	// 3단계 표 점수 셀 스타일
	threeLevelScoreCell: {
		fontWeight: 600,
		color: '#0066cc',
	}
};

// ClassName용 공통 스타일 정의
// 여러 스타일 파일에서 중복되던 루브릭 테이블 관련 스타일들을 통합
export const rubricTableClasses: (theme: Theme) => Record<string, CSSInterpolation> = (theme) => ({
	// 왼쪽 배경 박스 (평가항목 컬럼)
	bgLeftBox: {
		background: 'rgb(250, 250, 250)',
		borderRight: '1px solid #cbcbcb'
	},

	// 루브릭 셀 배경색
	rubricCellBgColor: {
		background: '#F2F7F3',
	},

	// 점수 셀 스타일
	scoreCell: {
		background: '#F2F7F3',
	},

	// 평가 테이블 박스
	evaluationTableBox: {
		marginBottom: "0px",
		'&.MuiPaper-root': {
			borderTop: '1px solid #cbcbcb',
			boxShadow: 'none',
			borderRadius: 0,
			'& table': {
				tableLayout: 'fixed'
			},
			'& .MuiTableCell-head': {
				fontSize: '1rem',
				color: '#054030',
				letterSpacing: '-0.5px',
				fontWeight: 700,
				padding: '0 5px',
				boxSizing: 'border-box',
				height: "44px",
				background: '#F2F7F3',
				borderBottom: '1px solid #cbcbcb',
				'& span': {
					fontSize: '1rem',
					fontWeight: 'normal'
				},
			},
			'& .MuiTableCell-body': {
				fontSize: '1rem',
				color: '#000',
				letterSpacing: '-0.5px',
				padding: '0 5px',
				boxSizing: 'border-box',
				borderBottom: '1px solid #cbcbcb',
			}
		}
	},

	// 평가 테이블 배경색 (셀)
	evaluationTableBgColor: {
		height: '140px !important',
		borderRight: '1px solid #cbcbcb',
		'& > .MuiTypography-root': {
			fontWeight: 700,
			fontSize: '1rem',
		}
	},

	// 루브릭 이름 항목 텍스트
	rubricNameItem: {
		'&.MuiTypography-root': {
			fontSize: '1rem',
			fontWeight: 700,
			color: '#0126fc',
		}
	},

	// 점수 이름 폰트
	fontScoreName: {
		'&.MuiTypography-root': {
			fontSize: '16px !important',
			fontWeight: '500 !important',
			color: '#01261C !important',
		}
	},

	// 평가 기준 폰트
	fontScoreStandard: {
		'&.MuiTypography-root': {
			fontSize: '1rem !important',
			color: '#000 !important',
			fontWeight: '400 !important',
		}
	},

	// 점수 표시 (일반)
	scoreMark: {
		'&.MuiTypography-root': {
			fontSize: '1.125rem',
			fontWeight: 700,
			'& span': {
				fontSize: '0.813rem',
				fontWeight: 400,
				marginLeft: 5
			}
		}
	},

	// 점수 표시 (타입 1)
	scoreMark1: {
		'&.MuiTypography-root': {
			fontSize: '1rem',
			color: '#01261C',
			fontWeight: 700,
			letterSpacing: '-0.5px'
		}
	},

	// 점수 입력 필드
	scoreMarkInput: {
		padding: '20px 5px',
		whiteSpace: 'normal',
		'& .MuiFormControl-root': {
			width: '100%',
			boxSizing: 'border-box',
			background: '#fff',
		},
		'& .MuiOutlinedInput-root': {
			height: "74px",
			'&.Mui-focused fieldset': {
				borderColor: '#2F80ED',
			}
		},
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: '#fff',
		}
	},

	// 점수 입력 필드 (여러 줄)
	scoreMarkInputMulti: {
		'& .MuiOutlinedInput-root': {
			height: "120px",
		},
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: '#fff',
		}
	},

	// 테두리 라인
	borderLine: {
		borderRight: '0px !important',
	},

	// 루브릭 테이블 기본
	rubricTable: {
		marginBottom: '30px'
	},

	// 기본 테이블
	tableBox: {
		margin: "20px 0",
		'&.MuiPaper-root': {
			borderTop: '1px solid #cbcbcb',
			boxShadow: 'none',
			borderRadius: 0,
			'& table': {
				tableLayout: 'fixed'
			},
			'& .MuiTableCell-head': {
				fontSize: '0.875rem',
				color: '#555',
				padding: '0 5px',
				boxSizing: 'border-box',
				height: "44px",
				fontWeight: 700,
				borderBottom: '1px solid #cbcbcb',
			},
			'& .MuiTableCell-body': {
				fontSize: '1rem',
				color: '#323232',
				height: "50px",
				padding: '0 5px',
				boxSizing: 'border-box',
				borderBottom: '1px solid #cbcbcb',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
				/* borderBottom: '1px solid rgba(119, 119, 119, 0.7)',
				borderRight: '1px solid rgba(119, 119, 119, 0.7)',
				borderLeft: '1px solid rgba(119, 119, 119, 0.7)', */
			}
		}
	},

	// 테이블 박스2 (export용)
	tableBox2: {
		marginBottom: "20px",
		'&.MuiPaper-root': {
			borderTop: '1px solid #cbcbcb',
			boxShadow: 'none',
			borderRadius: 0,
			'& table': {
				tableLayout: 'fixed'
			},
			'& .MuiTableCell-head': {
				fontSize: '1rem',
				color: '#054030',
				letterSpacing: '-0.5px',
				fontWeight: 700,
				padding: '0 5px',
				boxSizing: 'border-box',
				height: "44px",
				background: '#F2F7F3',
				borderBottom: '1px solid #cbcbcb',
			},
			'& .MuiTableCell-body': {
				fontSize: '1rem',
				color: '#000',
				letterSpacing: '-0.5px',
				padding: '0 5px',
				boxSizing: 'border-box',
				borderBottom: '1px solid #cbcbcb',
			}
		}
	},

	// 테이블 박스 상단 마진
	tableBoxMarginTop: {
		marginTop: "20px",
	},

	// 테이블 박스 (중앙 정렬, 90% 너비)
	tableBoxCentered90: {
		marginTop: "20px",
		marginLeft: 'auto',
		marginRight: 'auto',
		width: '90%',
		'&.MuiPaper-root': {
			borderTop: '1px solid rgba(119, 119, 119, 0.7)',
			boxShadow: 'none',
			borderRadius: 0,
			'& table': {
				tableLayout: 'fixed'
			},
			'& .MuiTableCell-head': {
				fontSize: '0.875rem',
				color: '#323232',
				letterSpacing: '-0.5px',
				fontWeight: 'bold',
				padding: '0 5px',
				boxSizing: 'border-box',
				height: "54px",
				borderBottom: '1px solid rgba(119, 119, 119, 0.7)',
				'& span': {
					fontSize: '0.875rem',
					fontWeight: 'normal'
				},
				'@media all and (max-width: 1550px)': {
					fontSize: '0.875rem',
					'& span': {
						fontSize: '0.688rem',
					}
				},
			},
			'& .MuiTableCell-body': {
				color: '#323232',
				height: "54px",
				letterSpacing: '-0.5px',
				padding: '0 5px',
				boxSizing: 'border-box',
				borderBottom: '1px solid rgba(119, 119, 119, 0.7)',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
				'@media all and (max-width: 1550px)': {
					fontSize: '0.875rem',
				},
				'& .MuiTypography-root': {
					color: '#323232',
					letterSpacing: '-0.5px',
				}
			}
		}
	},

	// 테이블 박스 (중앙 정렬, 95% 너비)
	tableBoxCentered95: {
		marginTop: "20px",
		marginLeft: 'auto',
		marginRight: 'auto',
		width: '95%',
		'&.MuiPaper-root': {
			borderTop: '1px solid #cbcbcb',
			boxShadow: 'none',
			borderRadius: 0,
			'& table': {
				tableLayout: 'fixed'
			},
			'& .MuiTableCell-head': {
				fontSize: '0.875rem',
				color: '#555',
				letterSpacing: '-0.5px',
				fontWeight: 400,
				padding: '0 5px',
				boxSizing: 'border-box',
				height: "50px",
				borderBottom: '1px solid #cbcbcb',
				'& span': {
					fontSize: '0.875rem',
					fontWeight: 'normal'
				},
				'@media all and (max-width: 1550px)': {
					fontSize: '0.875rem',
					'& span': {
						fontSize: '0.688rem',
					}
				},
			},
			'& .MuiTableCell-body': {
				color: '#323232',
				height: "50px",
				letterSpacing: '-0.5px',
				padding: '0 5px',
				boxSizing: 'border-box',
				borderBottom: '1px solid #cbcbcb',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
				'@media all and (max-width: 1550px)': {
					fontSize: '0.875rem',
				},
				'& .MuiTypography-root': {
					fontSize: '0.75rem',
					color: '#323232',
					letterSpacing: '-0.5px',
				}
			}
		}
	},

	// 테이블 박스 (중앙 정렬, 97% 너비)
	tableBoxCentered97: {
		marginTop: "20px",
		marginLeft: 'auto',
		marginRight: 'auto',
		width: '97%',
		'&.MuiPaper-root': {
			borderTop: '1px solid #cbcbcb',
			boxShadow: 'none',
			borderRadius: 0,
			'& table': {
				tableLayout: 'fixed'
			},
			'& .MuiTableCell-head': {
				fontSize: '0.875rem',
				color: '#555',
				letterSpacing: '-0.5px',
				fontWeight: 400,
				padding: '0 5px',
				boxSizing: 'border-box',
				height: "50px",
				borderBottom: '1px solid #cbcbcb',
				'& span': {
					fontSize: '0.875rem',
					fontWeight: 'normal'
				},
			},
			'& .MuiTableCell-body': {
				color: '#111',
				height: "50px",
				letterSpacing: '-0.5px',
				padding: '0 5px',
				boxSizing: 'border-box',
				borderBottom: '1px solid #cbcbcb',
				fontWeight: 400,
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
				'& .MuiTypography-root': {
					color: '#323232',
					letterSpacing: '-0.5px',
				}
			}
		}
	},
});

